'use client';

import { CustomEventName, GTMConstants, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import Flip from '@fe-common-ui/Flip';
import { formatTime } from '@fe-common-utils/libs/time';
import { GETv1NewsCategory } from '@fe-news/api/news';
import { NewsCategory } from '@fe-news/constants/categories';
import { NEWS_CATEGORIES } from '@fe-news/constants/news/category-id';
import { styled } from '@linaria/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const DynamicNewsBadge = dynamic(() => import('@fe-common-ui/Button').then(module => module.NewsLabel), {
  ssr: true
});

const ANUE_LIVE_LIMIT = 10;
const CAROUSEL_SPEED = 5;

const Container = styled.div`
  margin-bottom: 22px;
  background-color: rgba(226, 232, 241, 0.5);
  border-radius: 8px;

  .anue-live-flip {
    height: 40px;
  }
`;

const CarouselItem = styled(Link)`
  height: 40px;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const Title = styled.h3`
  color: black;
  letter-spacing: 0.5px;
  display: inline;
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 680px;
`;

const LabelTime = styled.div`
  ::after {
    content: '';
    width: 2px;
    height: 16px;
    background-color: rgba(0, 65, 143, 0.1);
    margin: 0 8px;
    display: inline-block;
    vertical-align: middle;
  }
`;

const Time = styled.div`
  color: #848d97;
  letter-spacing: 0.5px;
`;

const fetchAnueLiveData = async (): Promise<News[]> => {
  try {
    const response = await GETv1NewsCategory({
      catSlug: NewsCategory.ANUE_LIVE,
      limit: ANUE_LIVE_LIMIT
    });

    return response?.items?.data || [];
  } catch {
    console.error('[API ERROR]: 速報跑馬燈');
    return [];
  }
};

enum NewsLiveLabel {
  TWSTOCK = '台股',
  CRYPTO = '區塊鏈',
  INTERNATIONAL = '國際股',
  FOREX = '外匯',
  REVENUE = '營收'
}

const LABEL_COLORS = {
  [NewsLiveLabel.TWSTOCK]: '#007aff',
  [NewsLiveLabel.CRYPTO]: '#805cff',
  [NewsLiveLabel.INTERNATIONAL]: '#ff6f0f',
  [NewsLiveLabel.FOREX]: '#14bdff',
  [NewsLiveLabel.REVENUE]: '#ffb43e'
};

type NewsCategoryKey = keyof typeof NEWS_CATEGORIES;

const findTabCategory = (categoryId: number) => {
  const categories = Object.keys(NEWS_CATEGORIES) as NewsCategoryKey[];
  const category = categories.reduce((response, cat: NewsCategoryKey) => {
    if (NEWS_CATEGORIES[cat].includes(categoryId)) return cat;
    return response;
  }, '' as NewsCategoryKey);

  return category
    ? {
        label: NewsLiveLabel[category],
        color: LABEL_COLORS[NewsLiveLabel[category]]
      }
    : {};
};

const onClick = (newsId: number, newsTitle: string) => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
    [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_快報`,
    [GTMEventKey.CLICK_ITEM]: `${newsId}_${newsTitle}`
  });
};

const NewsLiveCarousel = () => {
  const [data, setData] = useState<null | News[]>(null);

  useEffect(() => {
    const fetchData = async () => {
      const dataFetched = await fetchAnueLiveData();
      setData(dataFetched);
    };

    fetchData();
  }, []);

  return (
    <Container>
      <Flip
        carouselNumber={data?.length ?? 0}
        bodyClassName='anue-live-flip'
        stopSeconds={CAROUSEL_SPEED}
        delayPlaySeconds={1}
      >
        {data?.map(({ title, newsId, publishAt, categoryId }) => {
          const url = `/news/id/${newsId}`;
          const { label, color } = findTabCategory(categoryId);

          return (
            <CarouselItem key={newsId} href={url} title={title} onClick={() => onClick(newsId, title)}>
              {label && (
                <DynamicNewsBadge color={color} marginRight={8}>
                  {label}
                </DynamicNewsBadge>
              )}
              <LabelTime>
                <Time>{formatTime(publishAt, 'HHMM')}</Time>
              </LabelTime>
              <TitleWrapper>
                <Title>{title}</Title>
              </TitleWrapper>
            </CarouselItem>
          );
        })}
      </Flip>
    </Container>
  );
};

export default NewsLiveCarousel;
